/**
 * GameStateManager - Manages game state transitions
 * Replaces multiple boolean flags with a proper state machine
 */

import { logger } from "../utils/logger";

/**
 * Game state enum - represents all possible game states
 */
export type GameState =
  | "waiting_to_start"  // Level intro screen showing
  | "playing"           // Normal gameplay
  | "showing_hint"      // Hint overlay showing
  | "on_arrow_tile"     // Being pushed by arrow tiles
  | "dead"              // Player died
  | "won";              // Level completed

/**
 * Game state manager state
 */
export type GameStateManagerState = {
  currentState: GameState;
  previousState: GameState | null;
};

/**
 * Create initial game state manager
 */
export function createGameStateManager(): GameStateManagerState {
  return {
    currentState: "waiting_to_start",
    previousState: null,
  };
}

/**
 * Transition to a new state
 * Returns true if transition was successful, false if invalid
 */
export function transitionTo(
  state: GameStateManagerState,
  newState: GameState,
  reason?: string
): boolean {
  // Validate transition (optional - can add more validation here)
  const oldState = state.currentState;

  // Log state transition
  /*  if (reason) {
     logger.debug(
       `[STATE] Transition: ${oldState} -> ${newState} (${reason})`
     );
   } */

  state.previousState = state.currentState;
  state.currentState = newState;
  return true;
}

/**
 * Check if player can process input in current state
 */
export function canProcessInput(state: GameStateManagerState): boolean {
  return (
    state.currentState === "playing" ||
    state.currentState === "on_arrow_tile"
  );
}

/**
 * Check if game is over (dead or won)
 */
export function isGameOver(state: GameStateManagerState): boolean {
  return state.currentState === "dead" || state.currentState === "won";
}

/**
 * Check if player is waiting to start
 */
export function isWaitingToStart(state: GameStateManagerState): boolean {
  return state.currentState === "waiting_to_start";
}

/**
 * Check if hint is showing
 */
export function isShowingHint(state: GameStateManagerState): boolean {
  return state.currentState === "showing_hint";
}

/**
 * Check if player is on arrow tile
 */
export function isOnArrowTile(state: GameStateManagerState): boolean {
  return state.currentState === "on_arrow_tile";
}

/**
 * Check if player is dead
 */
export function isDead(state: GameStateManagerState): boolean {
  return state.currentState === "dead";
}

/**
 * Check if player has won
 */
export function hasWon(state: GameStateManagerState): boolean {
  return state.currentState === "won";
}

/**
 * Get current state
 */
export function getCurrentState(state: GameStateManagerState): GameState {
  return state.currentState;
}
